import Vue from 'vue';
import { has } from '../../utils.js';

/**
 * A store for handling the play mode state - non persisting store
 */
const state = {
  history: [], // The play mode history { element, option, modifications: { id1: { newValue, oldValue }, id2: { newValue, oldValue }}}
  values: {}, // The variables values are in an associative array { id: value }
  beforeValues: {},
  visits: {},
  audioLevel: 1, // The audio volume of the audio asset references
};

const getters = {
  /**
   * Return the value of a variable
   * @param   {String}  id  The variable id
   * @returns {mixed}
   */
  getValue(state) {
    return id => {
      return state.values[id];
    };
  },

  /**
   * Return all variable ids and their values
   * @returns {Object}
   */
  getValues(state) {
    return state.values;
  },

  /**
   * Return the last entry in history.
   * @returns {Object}
   */
  getLastEntry(state) {
    if (state.history.length === 0) return null;
    return state.history[state.history.length - 1];
  },

  /**
   * Returns the number of history items
   * @return {Number}
   */
  getEntriesNumber(state) {
    return state.history.length;
  },

  /**
   * Returns the audio level
   * @returns {Number}
   */
  getAudioLevel(state) {
    return state.audioLevel;
  },

  getVisits(state) {
    return id => state.visits[id] ?? 0;
  },

  getAllVisits(state) {
    return state.visits;
  },
};

const actions = {};

const mutations = {
  /**
   * Set the variable values
   * @param {array}  values  The new values in the form of an associative array
   */
  setValues(state, { values }) {
    Object.entries(values).forEach(([id, value]) => {
      state.values[id] = value;
    });
  },

  /**
   * Remove a variable
   * @param {String} id   The variable name
   */
  delete(state, { id }) {
    delete state.values[id];
  },

  /**
   * Create a new entry in history
   */
  pushEntry(state, { entry }) {
    console.log('Pushing entry:', entry);
    state.history.push(entry);
  },

  /**
   * Update a history entry
   */
  updateEntry(state, { modifications }) {
    if (state.history.length > 0) {
      const entry = state.history[state.history.length - 1];
      console.log('Updating entry:', entry);
      entry.modifications = modifications;
    }
  },

  /**
   * Deletes the last entry in history
   */
  popEntry(state) {
    const entry = state.history.pop();
    // Return the state to the previous values
    if (has(entry, 'modifications')) {
      Object.entries(entry.modifications).forEach(([id, valuePair]) => {
        const { oldValue } = valuePair;
        state.values[id] = oldValue;
      });
    }
  },

  /**
   * Deletes all entries
   */
  clearEntries(state) {
    state.history = [];
  },

  /**
   * Sets the audio level
   * @param {Number} level The audio level between [0, 1]
   */
  setAudioLevel(state, { level }) {
    state.audioLevel = level;
  },

  setVisits(state, { id, value }) {
    state.visits[id] = value;
  },

  initVisits(state, { ids }) {
    const visits = {};
    ids.forEach(id => {
      visits[id] = 0;
    });
    state.visits = visits;
  },

  incrementVisits(state, { id }) {
    if (!(id in state.visits)) {
      state.visits[id] = 1;
    } else {
      state.visits[id] += 1;
    }
    return state.visits[id];
  },

  decrementVisits(state, { id }) {
    if (!(id in state.visits)) {
      state.visits[id] = 0;
    } else if (state.visits[id] > 0) {
      state.visits[id] -= 1;
    }
    return state.visits[id];
  },
};

const module = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default module;
